import CtaArrow from "@/components/svg/CtaArrow";
import { isFilled } from "@prismicio/client";
import { PrismicNextImage, PrismicNextLink } from "@prismicio/next";
import { PrismicRichText } from "@prismicio/react";
import Card from "./Card";
import Info, { richTextBase } from "./Info";

/**
 * @typedef {import("@prismicio/client").Content.AztkPresentationSlice} AztkPresentationSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<AztkPresentationSlice>} AztkPresentationProps
 * @type {import("react").FC<AztkPresentationProps>}
 */
const AztkPresentationDefault = ({ slice }) => {
  const { sub_header, title, text, picture, infos, cta } = slice.primary;
  const filledInfos = Array.isArray(infos)
    ? infos.filter(
      (item) => isFilled.richText(item.title) || isFilled.richText(item.text),
    )
    : [];
  const hasContent = filledInfos.length > 0 || isFilled.link(cta);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="contre-uno bg-beige-30 padding-x padding-y-md"
    >
      <div className="framing-lg flex flex-col gap-10">
        {(isFilled.keyText(sub_header) ||
          isFilled.richText(title) ||
          isFilled.richText(text)) && (
            <div className="flex flex-col gap-2.5 lg:max-w-[567px]">
              <div className="flex flex-col gap-1.5">
                {isFilled.keyText(sub_header) && (
                  <p className="section-text-n text-rouge">{sub_header}</p>
                )}
                {isFilled.richText(title) && (
                  <PrismicRichText
                    field={title}
                    components={{
                      heading2: ({ children }) => (
                        <h2 className="h2-n lg:dk text-bleu">{children}</h2>
                      ),
                      heading3: ({ children }) => (
                        <h3 className="h2-n lg:dk text-bleu">{children}</h3>
                      ),
                    }}
                  />
                )}
              </div>
              {isFilled.richText(text) && (
                <PrismicRichText
                  field={text}
                  components={{
                    ...richTextBase,
                    paragraph: ({ children }) => (
                      <p className="p-lg-n text-bleu not-last:mb-5">{children}</p>
                    ),
                  }}
                />
              )}
            </div>
          )}

        {(isFilled.image(picture) || hasContent) && (
          <div className="flex flex-col gap-10 lg:flex-row lg:gap-[70px]">
            {isFilled.image(picture) && (
              <div className="relative h-[280px] w-full shrink-0 overflow-hidden rounded-[10px] lg:h-auto lg:w-[45.6%] lg:self-stretch lg:rounded-[20px]">
                <PrismicNextImage
                  field={picture}
                  className="absolute inset-0 h-full w-full object-cover"
                  sizes="(max-width: 1024px) 100vw, 575px"
                />
              </div>
            )}

            <div className="flex flex-col gap-10">
              {hasContent && (
                <div className="flex min-w-0 flex-1 flex-col items-start gap-4">
                  {filledInfos.map((item, index) => (
                    <Info key={`info-${index}`} item={item} />
                  ))}

                </div>
              )}
              {isFilled.link(cta) && (
                <PrismicNextLink
                  prefetch={false}
                  field={cta}
                  className="cta cta-text w-fit bg-rouge text-beige-30"
                >
                  {cta.text}
                  <CtaArrow className="text-beige-30" />
                </PrismicNextLink>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

/**
 * @type {import("react").FC<AztkPresentationProps>}
 */
const AztkPresentationCard = ({ slice }) => {
  const { sub_header, title, text, picture, infos, info, card_text } =
    slice.primary;
  const filledInfos = Array.isArray(infos)
    ? infos.filter(
      (item) => isFilled.richText(item.title) || isFilled.richText(item.text),
    )
    : [];
  const hasContent =
    filledInfos.length > 0 ||
    isFilled.richText(info) ||
    isFilled.richText(card_text);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="contre-uno bg-beige-30 padding-x padding-y-md"
    >
      <div className="framing-lg flex flex-col gap-10">
        {(isFilled.keyText(sub_header) ||
          isFilled.richText(title) ||
          isFilled.richText(text)) && (
            <div className="flex flex-col gap-2.5 lg:max-w-[575px]">
              <div className="flex flex-col gap-1.5">
                {isFilled.keyText(sub_header) && (
                  <p className="section-text-n text-rouge">{sub_header}</p>
                )}
                {isFilled.richText(title) && (
                  <PrismicRichText
                    field={title}
                    components={{
                      heading2: ({ children }) => (
                        <h2 className="h2-n lg:dk text-bleu">{children}</h2>
                      ),
                      heading3: ({ children }) => (
                        <h3 className="h2-n lg:dk text-bleu">{children}</h3>
                      ),
                    }}
                  />
                )}
              </div>
              {isFilled.richText(text) && (
                <PrismicRichText
                  field={text}
                  components={{
                    ...richTextBase,
                    paragraph: ({ children }) => (
                      <p className="p-lg-n text-bleu not-last:mb-5">{children}</p>
                    ),
                  }}
                />
              )}
            </div>
          )}

        {(isFilled.image(picture) || hasContent) && (
          <div className="flex flex-col gap-15 lg:flex-row lg:gap-[70px]">
            {isFilled.image(picture) && (
              <div className="relative h-[280px] w-full shrink-0 overflow-hidden rounded-[10px] lg:h-auto lg:w-[45.6%] lg:self-stretch lg:rounded-[20px]">
                <PrismicNextImage
                  field={picture}
                  className="absolute inset-0 h-full w-full object-cover"
                  sizes="(max-width: 1024px) 100vw, 575px"
                />
              </div>
            )}

            <div className="flex flex-col gap-8">
              {hasContent && (
                <div className="flex min-w-0 flex-1 flex-col items-start gap-4">
                  {filledInfos.map((item, index) => (
                    <Info key={`info-${index}`} item={item} />
                  ))}
                  {isFilled.richText(info) && (
                    <PrismicRichText
                      field={info}
                      components={{
                        ...richTextBase,
                        heading4: ({ children }) => (
                          <h4 className="p-base-n md text-bleu not-first:mt-5">
                            {children}
                          </h4>
                        ),
                        paragraph: ({ children }) => (
                          <p className="p-base-n text-bleu">{children}</p>
                        ),
                      }}
                    />
                  )}
                </div>
              )}
              <Card text={card_text} />
            </div>

          </div>
        )}
      </div>
    </section>
  );
};

const AztkPresentation = ({ slice }) => {
  switch (slice.variation) {
    case "card":
      return <AztkPresentationCard slice={slice} />;
    default:
      return <AztkPresentationDefault slice={slice} />;
  }
};

export default AztkPresentation;
